import React from "react";
import HelpButton from "./HelpButton";
import {
  REACHES_MEMBERS,
  CONSEQUENCE,
  STANDING_WARNING,
  STATE_TAG,
  wordCount,
} from "./settingsMeta";

/**
 * One switch and everything that must remain readable once its help text has
 * moved to hover.
 *
 * 🔴 WHAT NEVER HIDES. The founder asked for the writing to move to a hover,
 * and most of it does. These four do not, because an operator needs them
 * BEFORE deciding to flip, and a hover is not a place you look before you know
 * you need to:
 *   1. the label and the raw flag key (the key is what an operator searches)
 *   2. a one-clause consequence, on any switch that reaches real members
 *   3. the red pill on those same switches
 *   4. a standing warning, hard-coded, e.g. "Turn this OFF before any store
 *      submission" on daily_cadence_test_v1
 *
 * The amber strip is the fifth thing, and the only one that is not always on
 * screen: it appears at the MOMENT a live-reaching switch is changed, holding
 * that flag's full help text unabridged. That is the right time to read a
 * consequence. As permanent page furniture it was wallpaper that everybody
 * scrolled past; as a response to an action it is unmissable.
 */
const FlagRow = ({ def, value, dirty, editable, helpOpen, onToggleHelp, onChange }) => {
  const live = REACHES_MEMBERS.has(def.name);
  const consequence = CONSEQUENCE[def.name];
  const standing = STANDING_WARNING[def.name];
  const tag = STATE_TAG[def.name];
  const changed = dirty !== undefined && dirty !== null;

  return (
    <div
      className="d-flex align-items-start justify-content-between gap-3 py-16"
      style={{ borderBottom: "1px solid var(--border-color)" }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-semibold">{def.label}</span>

          {live && (
            <span className="bg-danger-100 text-danger-600 px-8 py-2 radius-4 text-xs fw-semibold">
              Reaches members
            </span>
          )}
          {tag && (
            <span className="bg-neutral-200 text-secondary-light px-8 py-2 radius-4 text-xs fw-semibold">
              {tag}
            </span>
          )}

          {/* Kept visible because browser find no longer reaches collapsed
              text, and the key is what an operator who knows the system
              actually types. */}
          <code className="text-xs text-secondary-light">{def.name}</code>

          <HelpButton
            name={def.name}
            help={def.help}
            open={helpOpen}
            onToggle={onToggleHelp}
          />
        </div>

        {consequence && (
          <div className="text-sm text-secondary-light mt-1">{consequence}</div>
        )}

        {standing && (
          <div
            className="mt-2 px-12 py-8 text-sm fw-semibold"
            style={{
              background: "rgba(233, 162, 59, 0.12)",
              borderLeft: "3px solid #e9a23b",
              borderRadius: "0 8px 8px 0",
            }}
          >
            {standing}
          </div>
        )}

        {helpOpen && (
          <div
            id={`help-panel-${def.name}`}
            className="mt-2 px-12 py-12 text-sm text-secondary-light radius-8"
            style={{ background: "rgba(140, 80, 253, 0.06)", lineHeight: 1.6 }}
          >
            {def.help}
            <span className="d-block mt-2 text-xs" style={{ opacity: 0.7 }}>
              {wordCount(def.help)} words
            </span>
          </div>
        )}

        {/* The consequence, at the moment of the decision. */}
        {changed && live && (
          <div
            className="mt-2 px-12 py-12 text-sm radius-8"
            style={{
              background: "rgba(233, 162, 59, 0.12)",
              borderLeft: "3px solid #e9a23b",
              lineHeight: 1.6,
            }}
          >
            <b className="d-block mb-1">
              Turning this {dirty ? "ON" : "OFF"} reaches real members within a minute.
            </b>
            {def.help}
          </div>
        )}
      </div>

      <div className="form-check form-switch flex-shrink-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          /* 🔴 KEEP THIS INLINE STYLE. style.css forces .form-switch
             .form-check-input to 36x20px; without the override every switch
             on this page collapses back to the template size. */
          style={{ width: "3em", height: "1.5em", cursor: "pointer" }}
          checked={value === true}
          disabled={!editable}
          aria-label={def.label}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    </div>
  );
};

export default FlagRow;
